<template>
  <AdminLayout>
    <template #content>
      <div class="d-flex justify-content-between align-items-center mb-3">
        <h1 class="h4 mb-0">{{ pageTitle }} ({{ totalRows }})</h1>
        <div class="d-flex gap-2">
          <ModuleActivityButton :module="['sales', 'Sale', 'SaleItem']" :label="pageTitle" />

          <button
            type="button"
            class="btn btn-outline-secondary btn-sm btn-icon-label"
            :class="{ active: filtersVisible }"
            :title="filtersVisible ? 'Masquer les filtres' : 'Afficher les filtres'"
            @click="toggleFilters"
          >
            <i class="fa" :class="filtersVisible ? 'fa-eye-slash' : 'fa-filter'"></i>
            <span class="btn-label ms-1">Filtres</span>
          </button>
          <button
            v-if="!isQuotesMode"
            type="button"
            class="btn btn-outline-primary btn-sm btn-icon-label"
            @click="openTicketVerificationDialog"
          >
            <i class="fa fa-barcode"></i>
            <span class="btn-label ms-1">Vérifier un ticket</span>
          </button>
        </div>
      </div>

      <div v-if="errorMessage" class="alert alert-danger mb-3 py-2">
        {{ errorMessage }}
      </div>
      <div v-if="successMessage" class="alert alert-success mb-3 py-2">
        {{ successMessage }}
      </div>

      <!-- FILTRES -->
      <div v-show="filtersVisible" class="card module-filter-card mb-3">
        <div class="card-body py-2">
          <div class="row g-2 align-items-end">
            <div class="col-md-3">
              <label class="form-label small mb-1">Période (du)</label>
              <input
                type="date"
                class="form-control form-control-sm"
                v-model="filters.date_from"
              />
            </div>

            <div class="col-md-3">
              <label class="form-label small mb-1">Période (au)</label>
              <input
                type="date"
                class="form-control form-control-sm"
                v-model="filters.date_to"
              />
            </div>

            <div class="col-md-3">
              <label class="form-label small mb-1">Créé par (caissier)</label>
              <select
                class="form-select form-select-sm"
                v-model="filters.created_by"
              >
                <option value="">Tous</option>
                <option
                  v-for="u in cashiers"
                  :key="u.id"
                  :value="String(u.id)"
                >
                  {{ u.name }}
                </option>
              </select>
            </div>

            <div v-if="!isQuotesMode" class="col-md-3">
              <label class="form-label small mb-1">Statut</label>
              <select
                class="form-select form-select-sm"
                v-model="filters.status"
              >
                <option value="">Tous</option>
                <option value="draft">Brouillon</option>
                <option value="validated">Validée</option>
                <option value="cancelled">Annulée</option>
                <option value="abandoned">Abandonnée</option>
              </select>
            </div>

            <div class="col-12 module-filter-actions mt-2">
              <button
                type="button"
                class="btn btn-outline-secondary btn-sm btn-icon-label"
                @click="resetFilters"
              >
                <i class="fa fa-rotate-left"></i>
                <span class="btn-label ms-1">Réinitialiser</span>
              </button>
              <button
                type="button"
                class="btn btn-primary btn-sm btn-icon-label"
                @click="fetchSales"
              >
                <i class="fa fa-search"></i>
                <span class="btn-label ms-1">Filtrer</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <BootstrapDataTable
        :response="salesResponse"
        :columns="tableColumns"
        :loading="isLoading"
        :selectable="canBulkDeleteSales"
        v-model:selected-row-ids="bulkSelectedIds"
        :is-row-selectable="canSelectSaleForBulkDelete"
        :selection-disabled="bulkActionPending"
        :sort-by="table.sortBy"
        :sort-dir="table.sortDir"
        :per-page="table.perPage"
        :active-filter-count="activeFilterCount"
        state-key="sales.list"
        searchable
        v-model:search-value="filters.customer"
        search-placeholder="Rechercher un client..."
        detail-route-name="SaleShow"
        :empty-text="emptyText"
        @search-change="handleTableSearch"
        @sort-change="handleSortChange"
        @page-change="handlePageChange"
        @per-page-change="handlePerPageChange"
        @state-restored="Object.assign(table, { page: $event.page, perPage: $event.perPage })"
        @refresh="fetchSales"
      >
        <template #bulk-actions="{ selectedCount, isOverLimit }">
          <button
            type="button"
            class="btn btn-sm btn-outline-danger"
            :disabled="bulkActionPending || isOverLimit"
            @click="bulkDeleteSales"
          >
            <i class="fa fa-trash me-1" aria-hidden="true"></i>Supprimer {{ selectedCount }}
          </button>
        </template>

        <template #cell-status="{ row }">
          <span class="badge" :class="statusClass(row.status)">
            {{ statusLabel(row.status) }}
          </span>
        </template>

        <template #cell-customer_name="{ row }">
          {{ (row.customer_name || '').trim() ? row.customer_name : 'comptant' }}
        </template>

        <template #cell-creator="{ row }">
          <span class="text-muted" v-if="!row.creator">-</span>
          <span v-else>{{ row.creator.name }}</span>
        </template>

        <template #cell-total_amount="{ row }">
          {{ $format.currency(row.total_amount) }}
        </template>

        <template #cell-created_at="{ row }">
          {{ $format.date(row.created_at) }}
        </template>
      </BootstrapDataTable>

      <PrimevueDialog
        v-model:visible="ticketVerificationDialogVisible"
        :modal="true"
        :style="{ width: '480px', maxWidth: '96vw' }"
      >
        <template #header><h5 class="mb-0">Vérifier un ticket</h5></template>
        <p class="text-muted small">Scannez ou saisissez la signature affichée sous le code-barres du ticket.</p>
        <PrimevueInputText v-model.trim="ticketSignatureInput" class="w-100" placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx" @keyup.enter="verifyTicketSignature" />
        <div v-if="ticketVerificationError" class="alert alert-danger py-2 mt-3 mb-0">{{ ticketVerificationError }}</div>
        <div v-if="verifiedTicket" class="alert alert-success py-2 mt-3 mb-0">
          <strong>Ticket authentifié</strong><br />
          {{ verifiedTicket.reference }} · Position n°{{ verifiedTicket.daily_position }} · {{ $format.currency(verifiedTicket.total_amount) }}
        </div>
        <template #footer>
          <button type="button" class="btn btn-outline-secondary btn-sm" @click="ticketVerificationDialogVisible = false">
            <i class="fa fa-xmark me-1" aria-hidden="true"></i>Fermer
          </button>
          <AppLoadingButton class="btn btn-primary btn-sm" :loading="isVerifyingTicket" :disabled="!ticketSignatureInput" loading-label="Vérification…" @click="verifyTicketSignature">
            <i class="fa fa-shield-check me-1"></i> Vérifier
          </AppLoadingButton>
        </template>
      </PrimevueDialog>

      <PrimevueDialog
        v-model:visible="saleDialogVisible"
        :modal="true"
        :style="{ width: '980px', maxWidth: '96vw' }"
        :closable="!isSavingSale"
      >
        <template #header>
          <h5 class="mb-0">Nouvelle vente complète</h5>
        </template>

        <div class="row g-3">
          <div class="col-lg-5">
            <div class="mb-3">
              <label class="form-label small fw-semibold">Client</label>
              <PrimevueDropdown
                v-model="newSaleForm.client_id"
                :options="clientOptions"
                optionLabel="label"
                optionValue="id"
                class="w-100"
                placeholder="Client comptant"
                :loading="isLoadingClients"
                filter
                filterPlaceholder="Rechercher un client"
                @change="onSaleClientChanged"
              />
            </div>

            <div class="mb-3">
              <label class="form-label small fw-semibold">Nom client</label>
              <PrimevueInputText
                v-model="newSaleForm.customer_name"
                class="w-100"
                placeholder="comptant"
              />
            </div>

            <SaleProductsPicker
              :selected-product-ids="newSaleForm.items.map((item) => item.product_id)"
              :disabled="isSavingSale"
              @add="addSaleProduct"
            />
          </div>

          <div class="col-lg-7">
            <div class="d-flex justify-content-between align-items-center mb-2">
              <h6 class="mb-0">Produits sélectionnés</h6>
              <span class="small text-muted">{{ newSaleForm.items.length }} ligne(s)</span>
            </div>

            <PrimevueDataTable
              :value="newSaleForm.items"
              dataKey="_uid"
              responsiveLayout="scroll"
              class="p-datatable-sm mb-3"
            >
              <PrimevueColumn header="Produit">
                <template #body="{ data }">
                  <div class="d-flex flex-column">
                    <span class="fw-semibold">{{ data.product_name }}</span>
                    <small class="text-muted">{{ data.product_sku || '-' }}</small>
                  </div>
                </template>
              </PrimevueColumn>

              <PrimevueColumn header="Qté" style="width: 120px;">
                <template #body="{ data }">
                  <PrimevueInputNumber
                    v-model="data.quantity"
                    class="w-100"
                    inputClass="text-end"
                    :min="1"
                    :maxFractionDigits="0"
                  />
                </template>
              </PrimevueColumn>

              <PrimevueColumn header="PU" style="width: 140px;">
                <template #body="{ data }">
                  <PrimevueInputNumber
                    v-model="data.unit_price"
                    class="w-100"
                    inputClass="text-end"
                    mode="decimal"
                    :min="0"
                    :maxFractionDigits="2"
                  />
                </template>
              </PrimevueColumn>

              <PrimevueColumn header="Total" style="width: 120px;">
                <template #body="{ data }">
                  <div class="text-end fw-semibold">
                    {{ $format.currency(lineTotal(data)) }}
                  </div>
                </template>
              </PrimevueColumn>

              <PrimevueColumn header="" style="width: 64px;">
                <template #body="{ data }">
                  <button
                    type="button"
                    class="btn btn-outline-danger btn-sm btn-action-icon"
                    title="Retirer"
                    :disabled="isSavingSale"
                    @click="removeSaleLine(data._uid)"
                  >
                    <i class="fa fa-trash"></i>
                  </button>
                </template>
              </PrimevueColumn>

              <template #empty>
                <div class="text-center text-muted small py-3">
                  Ajoutez au moins un produit depuis la recherche.
                </div>
              </template>
            </PrimevueDataTable>

            <div class="row g-3">
              <div class="col-md-6">
                <label class="form-label small fw-semibold">Type de remise</label>
                <PrimevueDropdown
                  v-model="newSaleForm.discount_type"
                  :options="discountTypeOptions"
                  optionLabel="label"
                  optionValue="value"
                  class="w-100"
                  @change="normalizeDiscount"
                />
              </div>
              <div class="col-md-6">
                <label class="form-label small fw-semibold">Valeur remise</label>
                <PrimevueInputNumber
                  v-model="newSaleForm.discount_value"
                  class="w-100"
                  mode="decimal"
                  :min="0"
                  :maxFractionDigits="2"
                  :disabled="!newSaleForm.discount_type"
                />
              </div>
            </div>

            <div class="sale-dialog-summary my-3">
              <div class="d-flex justify-content-between">
                <span class="text-muted">Sous-total</span>
                <strong>{{ $format.currency(saleSubtotal) }}</strong>
              </div>
              <div class="d-flex justify-content-between">
                <span class="text-muted">Remise</span>
                <strong>{{ $format.currency(saleDiscountAmount) }}</strong>
              </div>
              <div class="d-flex justify-content-between fs-5">
                <span>Total à payer</span>
                <strong>{{ $format.currency(saleTotal) }}</strong>
              </div>
            </div>

            <div class="row g-3">
              <div class="col-md-6">
                <label class="form-label small fw-semibold">Mode de paiement *</label>
                <PrimevueDropdown
                  v-model="newSaleForm.payment_method"
                  :options="paymentMethodOptions"
                  optionLabel="label"
                  optionValue="value"
                  class="w-100"
                  @change="onPaymentMethodChanged"
                />
              </div>
              <div class="col-md-6">
                <label class="form-label small fw-semibold">Référence paiement</label>
                <PrimevueInputText
                  v-model="newSaleForm.payment_reference"
                  class="w-100"
                  :placeholder="requiresPaymentReference ? 'Obligatoire' : 'Facultatif'"
                />
              </div>

              <div class="col-md-6">
                <label class="form-label small fw-semibold">Montant reçu</label>
                <PrimevueInputNumber
                  v-model="newSaleForm.amount_received"
                  class="w-100"
                  mode="decimal"
                  :min="0"
                  :maxFractionDigits="2"
                />
              </div>
              <div class="col-md-6">
                <label class="form-label small fw-semibold">Montant rendu</label>
                <PrimevueInputNumber
                  :modelValue="saleChangeDue"
                  class="w-100"
                  mode="decimal"
                  :maxFractionDigits="2"
                  disabled
                />
              </div>

              <div v-if="newSaleForm.payment_method === 'credit'" class="col-12">
                <div class="credit-sale-help">
                  <div>
                    <span>Dette</span>
                    <strong>{{ $format.currency(selectedSaleClient?.current_debt || 0) }}</strong>
                  </div>
                  <div>
                    <span>Plafond</span>
                    <strong>{{ $format.currency(selectedSaleClient?.debt_limit || 0) }}</strong>
                  </div>
                  <div>
                    <span>Crédit disponible</span>
                    <strong>{{ $format.currency(selectedSaleClient?.available_credit || 0) }}</strong>
                  </div>
                  <p v-if="creditPaymentError">{{ creditPaymentError }}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <template #footer>
          <button
            type="button"
            class="btn btn-outline-secondary btn-sm"
            :disabled="isSavingSale"
            @click="closeCreateSaleDialog"
          >
            <i class="fa fa-xmark me-1" aria-hidden="true"></i>
            Annuler
          </button>
          <AppLoadingButton
            type="button"
            class="btn btn-primary btn-sm btn-icon-label"
            :loading="isSavingSale"
            :disabled="!newSaleForm.items.length"
            loading-label="Enregistrement..."
            @click="submitCompleteSale"
          >
            <i class="fa fa-save"></i>
            <span class="btn-label ms-1">
              Enregistrer la vente complète
            </span>
          </AppLoadingButton>
        </template>
      </PrimevueDialog>
    </template>
  </AdminLayout>
</template>

<script>
import { computed } from 'vue';
import { useStore } from 'vuex';
import SaleProductsPicker from './SaleProductsPicker.vue';
import { bulkActionViewMixin } from '@/support/bulkActions';
import { canBypassPermissions, hasPermission } from '@/support/permissions';
import {
  cleanFilterParams,
  countActiveFilters,
  extractApiTotal,
  extractApiList,
  loadModuleFilterState,
  saveModuleFilterValues,
  saveModuleFilterVisibility,
} from '@/support/moduleFilters';
import {
  currencyAmountDifference,
  isCurrencyAmountAtLeast,
  roundCurrencyAmount,
} from '@/utils/money';

const SALES_FILTER_MODULE = 'sales';
const QUOTES_FILTER_MODULE = 'sales_quotes';
const DEFAULT_FILTERS = {
  date_from: '',
  date_to: '',
  customer: '',
  created_by: '',
  status: '',
};

const createEmptySaleForm = () => ({
  client_id: null,
  customer_name: 'comptant',
  discount_type: null,
  discount_value: 0,
  payment_method: 'cash',
  payment_reference: '',
  amount_received: null,
  items: [],
});

export default {
  name: 'SalesView',
  mixins: [bulkActionViewMixin],
  components: {
    SaleProductsPicker,
  },
  data() {
    return {
      sales: [],
      salesResponse: null,
      isLoading: false,
      errorMessage: '',
      successMessage: '',
      table: {
        page: 1,
        perPage: 10,
        sortBy: 'created_at',
        sortDir: 'desc',
      },
      tableColumns: [
        { key: 'daily_position', label: 'Position', sortable: true, class: 'text-center' },
        { key: 'reference', label: 'Réf.' },
        { key: 'status', label: 'Statut', sortable: true, class: 'text-center' },
        { key: 'customer_name', label: 'Client' },
        { key: 'creator', label: 'Créé par' },
        { key: 'total_amount', label: 'Total', sortable: true, class: 'text-end' },
        { key: 'created_at', label: 'Date', sortable: true },
      ],

      filters: { ...DEFAULT_FILTERS },
      filtersVisible: false,

      cashiers: [], // [{id,name}]
      isLoadingCashiers: false,
      clients: [],
      isLoadingClients: false,
      saleDialogVisible: false,
      ticketVerificationDialogVisible: false,
      ticketSignatureInput: '',
      ticketVerificationError: '',
      verifiedTicket: null,
      isVerifyingTicket: false,
      isSavingSale: false,
      newSaleForm: createEmptySaleForm(),
      paymentMethodOptions: [
        { value: 'cash', label: 'Espèces' },
        { value: 'credit', label: 'Crédit' },
        { value: 'mobile_money', label: 'Mobile money' },
        { value: 'card', label: 'Carte bancaire' },
      ],
      discountTypeOptions: [
        { value: null, label: 'Aucune remise' },
        { value: 'percent', label: 'Pourcentage' },
        { value: 'amount', label: 'Montant' },
      ],
    };
  },
  setup() {
    const store = useStore();
    const currentUser = computed(() => store.getters['auth/currentUser']);
    const shopInfo = computed(() => store.getters['shop/shopInfo'] || {});
    const role = computed(() => currentUser.value?.role || '');

    const isCaissier = computed(() => role.value === 'caissier');
    const isGerant = computed(() => role.value === 'gerant');
    const canFinalizeClosedCashRegisterSale = computed(() => canBypassPermissions(currentUser.value));

    return {
      currentUser,
      shopInfo,
      role,
      isCaissier,
      isGerant,
      canFinalizeClosedCashRegisterSale,
    };
  },
  mounted() {
    this.restoreFilters();
    this.fetchCashiers();
    this.fetchSales();
  },
  watch: {
    '$route.name'() {
      this.restoreFilters();
      this.table.page = 1;
      this.fetchSales();
    },
    filters: {
      deep: true,
      handler(filters) {
        saveModuleFilterValues(this.filterModule, filters);
      },
    },
    filtersVisible(value) {
      saveModuleFilterVisibility(this.filterModule, value);
    },
  },
  computed: {
    isQuotesMode() {
      return this.$route.name === 'SalesQuotes';
    },
    filterModule() {
      return this.isQuotesMode ? QUOTES_FILTER_MODULE : SALES_FILTER_MODULE;
    },
    pageTitle() {
      return this.isQuotesMode ? 'Devis' : 'Ventes';
    },
    emptyText() {
      return this.isQuotesMode ? 'Aucun devis trouvé.' : 'Aucune vente trouvée.';
    },
    totalRows() {
      return extractApiTotal(this.salesResponse, this.sales.length);
    },
    activeFilterCount() {
      return countActiveFilters(
        this.normalizedFiltersForRoute(this.filters),
        this.defaultFiltersForRoute(),
        ['customer']
      );
    },
    canCreate() {
      return this.isCaissier || this.isGerant;
    },
    canBulkDeleteSales() {
      return hasPermission(this.currentUser, 'sales.cancel');
    },
    clientOptions() {
      return [
        { id: null, label: 'Client comptant', name: 'comptant' },
        ...this.clients.map((client) => ({
          id: client.id,
          label: client.phone ? `${client.name} · ${client.phone}` : client.name,
          name: client.name,
          is_system: !!client.is_system || client.type === 'system',
          debt_limit: Number(client.debt_limit || 0),
          current_debt: Number(client.current_debt || 0),
          available_credit: Number(client.available_credit || 0),
        })),
      ];
    },
    selectedSaleClient() {
      return this.clientOptions.find((option) => Number(option.id || 0) === Number(this.newSaleForm.client_id || 0)) || null;
    },
    saleSubtotal() {
      return this.roundSaleMoney(this.newSaleForm.items.reduce((total, item) => total + this.lineTotal(item), 0));
    },
    saleDiscountAmount() {
      const value = Number(this.newSaleForm.discount_value || 0);
      if (!this.newSaleForm.discount_type || value <= 0) return 0;
      if (this.newSaleForm.discount_type === 'amount') return this.roundSaleMoney(Math.min(value, this.saleSubtotal));
      return this.roundSaleMoney(Math.min(this.saleSubtotal * (value / 100), this.saleSubtotal));
    },
    saleTotal() {
      return this.roundSaleMoney(Math.max(this.saleSubtotal - this.saleDiscountAmount, 0));
    },
    requiresPaymentReference() {
      return ['mobile_money', 'card'].includes(this.newSaleForm.payment_method);
    },
    saleChangeDue() {
      if (this.newSaleForm.payment_method !== 'cash') return 0;
      return this.cashAmountDifference(this.newSaleForm.amount_received, this.saleTotal);
    },
    creditPaymentError() {
      if (this.newSaleForm.payment_method !== 'credit') return '';

      if (!this.selectedSaleClient?.id) {
        return 'Sélectionnez un client standard pour vendre à crédit.';
      }

      if (this.selectedSaleClient.is_system) {
        return 'Le crédit est interdit sur le client système.';
      }

      if (Number(this.selectedSaleClient.debt_limit || 0) <= 0) {
        return 'Ce client n’a pas de plafond dette configuré.';
      }

      if (this.saleTotal > this.roundSaleMoney(this.selectedSaleClient.available_credit || 0)) {
        return 'Le total dépasse le crédit disponible du client.';
      }

      return '';
    },
  },
  methods: {
    canSelectSaleForBulkDelete(sale) {
      return sale?.status === 'draft';
    },

    async bulkDeleteSales() {
      await this.performBulkAction({
        url: '/sales/bulk',
        method: 'delete',
        itemLabel: 'vente',
        confirmation: (count) => ({
          header: 'Supprimer les ventes',
          message: `Supprimer les ${count} ventes brouillon sélectionnées ?`,
          acceptLabel: `Supprimer ${count}`,
          acceptClass: 'p-button-danger',
        }),
        refresh: () => this.fetchSales(),
      });
    },

    restoreFilters() {
      const state = loadModuleFilterState(this.filterModule, this.defaultFiltersForRoute());
      this.filters = this.normalizedFiltersForRoute(state.values);
      this.filtersVisible = state.visible;
    },

    toggleFilters() {
      this.filtersVisible = !this.filtersVisible;
    },

    async fetchCashiers() {
      // ✅ adapte l’endpoint selon ton app
      // option A: GET /users?role=caissier
      // option B: GET /cashiers
      this.isLoadingCashiers = true;
      try {
        const res = await this.$api.get('/users', { params: { role: 'caissier' } });
        const data = res.data.data || res.data || [];
        this.cashiers = data.map((u) => ({ id: u.id, name: u.name }));
      } catch (e) {
        // si pas d’endpoint: tu peux laisser vide
        console.warn('Impossible de charger la liste des caissiers (optionnel).', e);
        this.cashiers = [];
      } finally {
        this.isLoadingCashiers = false;
      }
    },

    async fetchClients() {
      this.isLoadingClients = true;
      try {
        const response = await this.$api.get('/clients', {
          params: { per_page: 100, sort_by: 'name', sort_dir: 'asc' },
        });
        this.clients = extractApiList(response.data);
      } catch (error) {
        console.warn('Impossible de charger les clients.', error);
        this.clients = [];
      } finally {
        this.isLoadingClients = false;
      }
    },

    roundSaleMoney(value) {
      return roundCurrencyAmount(value, this.shopInfo);
    },

    cashAmountDifference(amount, total) {
      return currencyAmountDifference(amount, total, this.shopInfo);
    },

    isCashAmountSufficient(amount, total) {
      return isCurrencyAmountAtLeast(amount, total, this.shopInfo);
    },

    resetFilters() {
      this.filters = this.defaultFiltersForRoute();
      this.table.page = 1;
      this.fetchSales();
    },

    async fetchSales() {
      this.isLoading = true;
      this.errorMessage = '';

      try {
        const params = {
          ...cleanFilterParams(this.normalizedFiltersForRoute(this.filters)),
          paginate: 1,
          page: this.table.page,
          per_page: this.table.perPage,
          sort_by: this.table.sortBy,
          sort_dir: this.table.sortDir,
        };
        const response = await this.$api.get('/sales', { params });
        this.salesResponse = response.data;
        this.sales = extractApiList(response.data);
      } catch (error) {
        console.error('Erreur lors du chargement des ventes', error);
        this.errorMessage = 'Impossible de charger les ventes.';
      } finally {
        this.isLoading = false;
      }
    },

    defaultFiltersForRoute() {
      return this.normalizedFiltersForRoute(DEFAULT_FILTERS);
    },

    normalizedFiltersForRoute(filters) {
      return {
        ...filters,
        status: this.isQuotesMode ? 'draft' : filters.status,
      };
    },

    openCreateSaleDialog() {
      this.errorMessage = '';
      this.successMessage = '';
      this.newSaleForm = createEmptySaleForm();
      this.saleDialogVisible = true;
      this.fetchClients();
    },
    openTicketVerificationDialog() {
      this.ticketSignatureInput = '';
      this.ticketVerificationError = '';
      this.verifiedTicket = null;
      this.ticketVerificationDialogVisible = true;
    },
    async verifyTicketSignature() {
      if (!this.ticketSignatureInput || this.isVerifyingTicket) return;

      this.isVerifyingTicket = true;
      this.ticketVerificationError = '';
      this.verifiedTicket = null;
      try {
        const response = await this.$api.get(`/sales/verify-ticket/${encodeURIComponent(this.ticketSignatureInput)}`);
        this.verifiedTicket = response.data.data || response.data;
      } catch (error) {
        this.ticketVerificationError = error.response?.data?.errors
          ? Object.values(error.response.data.errors).flat().join(' ')
          : 'Impossible de vérifier ce ticket.';
      } finally {
        this.isVerifyingTicket = false;
      }
    },

    closeCreateSaleDialog() {
      if (this.isSavingSale) return;
      this.saleDialogVisible = false;
    },

    onSaleClientChanged() {
      const client = this.clientOptions.find((option) => Number(option.id || 0) === Number(this.newSaleForm.client_id || 0));
      this.newSaleForm.customer_name = client?.name || 'comptant';
    },

    addSaleProduct(payload) {
      const product = payload?.product;
      if (!product) return;

      const quantity = Math.max(Number(payload.quantity || 1), 1);
      const existing = this.newSaleForm.items.find((item) => Number(item.product_id) === Number(product.id));

      if (existing) {
        return;
      }

      this.newSaleForm.items.push({
        _uid: `sale-line-${product.id}-${Date.now()}`,
        product_id: product.id,
        product_name: product.name,
        product_sku: product.sku,
        quantity,
        unit_price: Number(product.effective_sale_price ?? product.sale_price ?? 0),
      });
    },

    removeSaleLine(uid) {
      this.newSaleForm.items = this.newSaleForm.items.filter((item) => item._uid !== uid);
    },

    lineTotal(item) {
      return Number(item.quantity || 0) * Number(item.unit_price || 0);
    },

    normalizeDiscount() {
      if (!this.newSaleForm.discount_type) {
        this.newSaleForm.discount_value = 0;
      }
    },

    onPaymentMethodChanged() {
      if (this.newSaleForm.payment_method !== 'cash') {
        this.newSaleForm.amount_received = this.newSaleForm.payment_method === 'credit' ? 0 : this.saleTotal;
      }
    },

    validateCompleteSaleForm() {
      if (!this.newSaleForm.items.length) {
        return 'Ajoutez au moins un produit.';
      }

      const invalidLine = this.newSaleForm.items.find((item) => Number(item.quantity || 0) < 1 || Number(item.unit_price || 0) < 0);
      if (invalidLine) {
        return 'Vérifiez les quantités et les prix des produits.';
      }

      if (this.requiresPaymentReference && !String(this.newSaleForm.payment_reference || '').trim()) {
        return 'Renseignez la référence du paiement.';
      }

      if (this.newSaleForm.payment_method === 'cash' && !this.isCashAmountSufficient(this.newSaleForm.amount_received, this.saleTotal)) {
        return 'Le montant reçu doit être supérieur ou égal au total à payer.';
      }

      if (this.creditPaymentError) {
        return this.creditPaymentError;
      }

      return '';
    },

    async submitCompleteSale() {
      if (this.isSavingSale) return;

      const validationMessage = this.validateCompleteSaleForm();
      if (validationMessage) {
        this.errorMessage = validationMessage;
        return;
      }

      this.isSavingSale = true;
      this.errorMessage = '';
      this.successMessage = '';
      let createdSale = null;

      try {
        const saleResponse = await this.$api.post('/sales', {
          ticket_signature_uid: this.generateTicketSignatureUid(),
          customer_name: this.newSaleForm.customer_name || 'comptant',
          client_id: this.newSaleForm.client_id || null,
          discount_type: this.newSaleForm.discount_type || null,
          discount_value: Number(this.newSaleForm.discount_value || 0),
          items: this.newSaleForm.items.map((item) => ({
            product_id: item.product_id,
            quantity: Math.max(Number(item.quantity || 1), 1),
            unit_price: Number(item.unit_price || 0),
          })),
        });

        createdSale = saleResponse.data.data;

        await this.$api.post(`/sales/${createdSale.id}/pay`, {
          payment_method: this.newSaleForm.payment_method,
          payment_reference: this.requiresPaymentReference
            ? String(this.newSaleForm.payment_reference || '').trim()
            : (String(this.newSaleForm.payment_reference || '').trim() || null),
          amount_received: this.newSaleForm.payment_method === 'cash'
            ? this.roundSaleMoney(this.newSaleForm.amount_received || this.saleTotal)
            : (this.newSaleForm.payment_method === 'credit' ? 0 : this.saleTotal),
          amount_returned: this.newSaleForm.payment_method === 'cash' ? this.saleChangeDue : 0,
        });

        this.saleDialogVisible = false;
        this.newSaleForm = createEmptySaleForm();
        this.successMessage = 'Vente complète enregistrée avec succès.';
        await this.fetchSales();
      } catch (error) {
        const errors = error.response?.data?.errors;
        const fallback = createdSale
          ? 'La vente a été créée en brouillon, mais le paiement ou la validation a échoué.'
          : "Une erreur est survenue lors de l'enregistrement de la vente.";
        this.errorMessage = errors ? Object.values(errors).flat().join(' ') : fallback;
      } finally {
        this.isSavingSale = false;
      }
    },
    generateTicketSignatureUid() {
      if (window.crypto?.randomUUID) return window.crypto.randomUUID();

      const bytes = new Uint8Array(16);
      window.crypto.getRandomValues(bytes);
      bytes[6] = (bytes[6] & 0x0f) | 0x40;
      bytes[8] = (bytes[8] & 0x3f) | 0x80;
      const hexadecimal = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
      return `${hexadecimal.slice(0, 8)}-${hexadecimal.slice(8, 12)}-${hexadecimal.slice(12, 16)}-${hexadecimal.slice(16, 20)}-${hexadecimal.slice(20)}`;
    },

    statusLabel(status) {
      switch (status) {
        case 'draft': return 'Brouillon';
        case 'validated': return 'Validée';
        case 'cancelled': return 'Annulée';
        case 'abandoned': return 'Abandonnée';
        default: return status;
      }
    },
    statusClass(status) {
      switch (status) {
        case 'draft': return 'bg-secondary';
        case 'validated': return 'bg-success';
        case 'cancelled': return 'bg-danger';
        case 'abandoned': return 'bg-secondary';
        default: return 'bg-secondary';
      }
    },

    async validateSale(sale) {
      const ok = await this.$confirmAction(`Valider la vente "${sale.reference}" ? Le stock sera mis à jour.`);
      if (!ok) return;

      try {
        await this.$api.post(`/sales/${sale.id}/validate`);
        await this.fetchSales();
      } catch (error) {
        const msg = error.response?.data?.errors
          ? Object.values(error.response.data.errors).flat().join(' ')
          : "Une erreur est survenue lors de la validation de la vente.";
        alert(msg);
      }
    },

    async cancelSale(sale) {
      const ok = await this.$confirmAction(`Annuler la vente "${sale.reference}" ? Le stock sera réajusté.`);
      if (!ok) return;

      try {
        await this.$api.post(`/sales/${sale.id}/cancel`);
        await this.fetchSales();
      } catch (error) {
        const msg = error.response?.data?.errors
          ? Object.values(error.response.data.errors).flat().join(' ')
          : "Une erreur est survenue lors de l'annulation de la vente.";
        alert(msg);
      }
    },

    async confirmDelete(sale) {
      const ok = await this.$confirmAction(`Voulez-vous vraiment supprimer la vente "${sale.reference}" ?`);
      if (!ok) return;

      try {
        await this.$api.delete(`/sales/${sale.id}`);
        await this.fetchSales();
      } catch (error) {
        const msg = error.response?.data?.errors
          ? Object.values(error.response.data.errors).flat().join(' ')
          : "Une erreur est survenue lors de la suppression de la vente.";
        alert(msg);
      }
    },

    goToShow(sale) {
      this.$router.push({ name: 'SaleShow', params: { id: sale.id } });
    },

    handleSortChange({ sortBy, sortDir }) {
      this.table.sortBy = sortBy;
      this.table.sortDir = sortDir;
      this.table.page = 1;
      this.fetchSales();
    },

    handlePageChange(page) {
      this.table.page = page;
      this.fetchSales();
    },

    handlePerPageChange(perPage) {
      this.table.perPage = perPage;
      this.table.page = 1;
      this.fetchSales();
    },

    handleTableSearch(search) {
      this.filters.customer = search;
      this.table.page = 1;
      this.fetchSales();
    },

    openEditDialog() {
      // tu avais la méthode dans ton code, je la laisse comme avant si tu l’utilises ailleurs
    },

    canEdit(sale) {
      return (this.isCaissier || this.isGerant) && sale.status === 'draft';
    },
    canValidate(sale) {
      return this.canFinalizeDraftSale(sale);
    },
    canCancel(sale) {
      return this.isGerant && sale.status === 'validated';
    },
    canDelete(sale) {
      return (this.isCaissier || this.isGerant) && sale.status === 'draft';
    },
    canFinalizeDraftSale(sale) {
      if (!sale || sale.status !== 'draft') return false;
      if (this.canFinalizeClosedCashRegisterSale) return true;
      if (!this.isCaissier) return false;

      return !this.isSaleFromClosedCashRegisterSession(sale);
    },
    isSaleFromClosedCashRegisterSession(sale) {
      const status = sale?.cash_register_session?.status;
      return Boolean(status && status !== 'open');
    },
  },
};
</script>

<style scoped>
.sale-dialog-summary {
  background: #f8f9fb;
  border: 1px solid #e7eaf0;
  border-radius: 8px;
  padding: 0.85rem 1rem;
}

.credit-sale-help {
  background: #f8fafc;
  border: 1px solid #dbeafe;
  border-radius: 8px;
  display: grid;
  gap: 0.65rem;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  padding: 0.85rem;
}

.credit-sale-help div {
  min-width: 0;
}

.credit-sale-help span {
  color: #64748b;
  display: block;
  font-size: 0.75rem;
  font-weight: 800;
}

.credit-sale-help strong {
  color: #0f172a;
  display: block;
  font-weight: 900;
  margin-top: 0.15rem;
}

.credit-sale-help p {
  color: #dc2626;
  font-size: 0.8rem;
  font-weight: 800;
  grid-column: 1 / -1;
  margin: 0;
}
</style>
